import {Component, Input} from '@angular/core';
import {Observable, forkJoin, of} from 'rxjs';
import {
  CtBinaryOperator, CTDatatablePaginationParameter,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelRouteData, CtModelService,
  CtModelType, CtSortOrderDescriptorParameter, SnackbarService
} from "@ctsolution/ct-framework";
import {CtWebapiGenericResponse, CtWebapiService, DataRequest} from "@ctsolution/ct-webapi";
import {CompanyPlaceRouteData} from "../../../_core/route-data/company-place.route-data";
import {CompanyInformationRouteData} from "../../../_core/route-data/company-information.route-data";
import {CtManagerService} from "../../../_core/lib/ct-manager.service";
import {CTConfigurationModel} from "../../../_core/classes/ct-configuration-model";

@Component({
  selector: 'app-company-place-list',
  templateUrl: './company-place-list.component.html',
  styleUrls:['./company-place-list.component.scss']
})
export class CompanyPlaceListComponent {

  @Input() masterConfiguration: CtModelConfiguration<any> | null = null;
  @Input() parentComponent: any; // Riferimento al componente parent CompanyEditComponent
  configurations: Array<CtModelConfiguration<any>> = [];
  companyPlaceConfiguration: CTConfigurationModel = CTConfigurationModel.create();

  // Promessa riusata: il lookup dei PlaceType si interroga una volta sola, non a ogni "+".
  private defaultPlaceTypeOid: Promise<number | null> | null = null;

  constructor(private _webapi: CtWebapiService, private _model: CtModelService<any>, private _snackbar: SnackbarService, private _managerService: CtManagerService) {
  }

  async ngOnInit() {

    await this._managerService.getComponentOverride("company", "companyPlaceConfiguration", this.companyPlaceConfiguration);

    if (this.masterConfiguration?.RouteData?.id) {

      this.setup();

    }

  }

  setup() {

    const body: CtModelDatatableOperators = CtModelDatatableOperators
      .create()
      .setFilters([
        CTModelDatatableFilter
          .create()
          .setField('Company.Oid')
          .setValue(this.masterConfiguration?.RouteData?.id)
          .setOperatorType(CtBinaryOperator.Equal)
      ])
      .setPagination(
        new CTDatatablePaginationParameter()
          .setSortOrders([
            CtSortOrderDescriptorParameter.create('Oid')
          ])
      )

    const request: DataRequest = new DataRequest()
      .setController([CompanyPlaceRouteData().controller!])
      .setAction('List')
      .setQueryParams({hideSpinner: true})
      .setBody(body)

    this._webapi
      .post<CtModelConfiguration<any>>(request)
      .subscribe((result: CtWebapiGenericResponse<CtModelConfiguration<any>>) => {

        this.configurations = new Array<CtModelConfiguration<any>>();

        if (!(result.Result?.DataSource ?? []).length) {

          //this.addNewConfiguration();  rimosso, momentaneamente non neccessario
          return;

        }

        result
          .Result
          .DataSource
          .forEach((ds: any) => {

            const configuration: CtModelConfiguration<any> = this.getDefaultConfiguration(ds.Oid).setDataSource(ds);

            this.configurations
              .push(configuration);

          })

      });

  }


  async addNewConfiguration() {

    const configuration: CtModelConfiguration<any> = this.getDefaultConfiguration();

    // Tipo indirizzo preselezionato dal MenuJson: stessa strada delle sedi gia' salvate, che arrivano
    // al form come DataSource. Senza dichiarazione (o se il tipo non esiste) la sede nasce vuota come oggi.
    const placeTypeOid = await this.resolveDefaultPlaceTypeOid();

    if (placeTypeOid) {

      configuration.setDataSource({ PlaceTypeOid: placeTypeOid });

    }

    this.configurations
      .push(configuration);

  }

  /**
   * Risolve a runtime l'Oid del PlaceType dichiarato in `defaultPlaceType`, cercandolo nel lookup per
   * campo e valore: niente Oid cablato nel JSON, perche' cambia da un ambiente all'altro.
   */
  private resolveDefaultPlaceTypeOid(): Promise<number | null> {

    const defaultPlaceType = this.companyPlaceConfiguration.defaultPlaceType;

    if (!defaultPlaceType?.field) return Promise.resolve(null);

    if (!this.defaultPlaceTypeOid) {

      const request: DataRequest = new DataRequest()
        .setController(['PlaceType'])
        .setAction('Lookup')
        .setQueryParams({hideSpinner: true})
        .setBody(CtModelDatatableOperators.create());

      this.defaultPlaceTypeOid = this._webapi
        .post<any>(request)
        .toPromise()
        .then((response: any) => {

          const placeTypes = (response?.Result?.DataSource ?? response?.Result ?? []) as any[];

          // Confronto senza distinzione di maiuscole: nel JSON si scrive "Sede Legale", il lookup
          // risponde "Sede legale", e un default che salta per una maiuscola non serve a nessuno.
          const placeType = placeTypes
            .find(elm => `${elm?.[defaultPlaceType.field]}`.toLowerCase() === `${defaultPlaceType.value}`.toLowerCase()) ?? null;

          if (!placeType) {

            console.warn(`Tipo indirizzo predefinito non trovato: nessun PlaceType con ${defaultPlaceType.field} = ${defaultPlaceType.value}.`);
            return null;

          }

          // Il lookup PlaceType non espone un Oid: il valore del control e' il suo `Code` (il CodeField del
          // modello JSON della sede), che per un PlaceTypeVO e' il numero dell'enum.
          const code = placeType.Oid ?? placeType.Code;

          return code != null && !isNaN(+code) ? +code : null;

        })
        .catch((error: any) => {

          console.error("Errore durante il recupero del tipo indirizzo predefinito:", error);
          return null;

        });

    }

    return this.defaultPlaceTypeOid;

  }

  private getDefaultRouteData = () => CtModelRouteData
    .create()
    .setModelType(CtModelType.FORM)
    .setController(`${window.location.origin}/assets/demo/company-place-model.json`);

  private getCompanyPlaceConfiguration = () => CtModelConfiguration
    .create()
    .setRouteData(
      CtModelRouteData
        .create()
        .setController(CompanyPlaceRouteData().controller)
    );

  private getDefaultConfiguration = (id: number | null = null) => CtModelConfiguration
    .create()
    .setRouteData(this.getDefaultRouteData())
    .setDefaultActionsEnabled(false)
    .setOnCreate((event: any) => {

      // Controlla se siamo in modalità edit verificando se esiste un id nel masterConfiguration
      const isEditMode = this.masterConfiguration?.RouteData?.id;
      
      // Se abbiamo il parent component, siamo in edit mode E il metodo submitAll esiste, usa il submit sincronizzato
      if (this.parentComponent && this.parentComponent.submitAll && isEditMode) {
        this.parentComponent.submitAll();
      } else {
        // Altrimenti usa il metodo tradizionale standalone
        const configuration: CtModelConfiguration<any> = this.getCompanyPlaceConfiguration();

        if (id) {

          configuration
            .RouteData
            ?.setId(id);

        }

        event
          .CompanyOid = this.masterConfiguration?.RouteData?.id ?? -1;

        this._model
          .putInfos(configuration, event)
          ?.subscribe(() => {

            this._snackbar.onSuccess();
            this.setup()

          });
      }

    })
    .setOnDelete((value: { idx: number }) => {

      if (!id) {

        this.configurations.pop();
        return;

      }

      const configuration: CtModelConfiguration<any> = this.getCompanyPlaceConfiguration();

      configuration
        .RouteData
        ?.setId(id);

      const controller: string | undefined | null = configuration.RouteData?.controller;

      if (!controller) return;

      const request: DataRequest = new DataRequest()
        .setController([controller, 'Delete'])
        .setAction(id.toString());

      this._webapi
        .delete(request)
        .subscribe(() => {

          this._snackbar.onSuccess();
          this.setup()

        });

    });

  /**
   * Metodo esposto per il submit sincronizzato dal parent component
   * Salva tutti i places in parallelo
   * @returns Observable<any>
   */
  submitAllPlaces(): Observable<any> {
    
    // Se non ci sono configurazioni (places), ritorno un Observable vuoto con successo
    if (!this.configurations || this.configurations.length === 0) {
      return of({ success: true, message: 'No places to save' });
    }

    // Array di Observable per ogni place da salvare
    const saveObservables: Observable<any>[] = [];

    this.configurations.forEach((config, index) => {
      
      // Se il form non esiste o non è valido, skippo (la validazione è già stata fatta dal parent)
      if (!config.form) {
        return;
      }

      // Creo un Observable per salvare questo specifico place
      const saveObservable = new Observable(observer => {
        
        const placeData = config.form!.value;
        placeData.CompanyOid = this.masterConfiguration?.RouteData?.id ?? -1;

        const configuration: CtModelConfiguration<any> = this.getCompanyPlaceConfiguration();

        // Se il place ha già un Oid (esiste nel datasource), lo setto per fare update
        const placeOid = config.DataSource?.Oid;
        if (placeOid) {
          configuration.RouteData?.setId(placeOid);
        }

        this._model
          .putInfos(configuration, placeData)
          ?.subscribe({
            next: (response) => {
              observer.next(response);
              observer.complete();
            },
            error: (err) => {
              console.error(`Errore durante il salvataggio del place ${index + 1}:`, err);
              observer.error(err);
            }
          });
      });

      saveObservables.push(saveObservable);
    });

    // Se non ci sono places da salvare, ritorno successo
    if (saveObservables.length === 0) {
      return of({ success: true, message: 'No places to save' });
    }

    // Eseguo tutti i salvataggi in parallelo con forkJoin
    return forkJoin(saveObservables);
  }

}
